/**
 * Reads `array[index]` where an out-of-range index is a DEFECT, not a value.
 *
 * `noUncheckedIndexedAccess` types every index read as `T | undefined`, which
 * is right for a lookup that can miss and noise for one that cannot — a
 * constant table's own row, the entry the caller pushed two lines up, the
 * element a length assertion already covered. Those reads come here, so the
 * failure is a named error at the read rather than a `TypeError` further down.
 *
 * Negative indices count from the end, like `Array.prototype.at`.
 *
 * Where a missing entry is a real outcome — a user-supplied index, a search
 * result, a sparse map — keep `array[i]` and handle the `undefined`. And do not
 * use this on an array whose elements are themselves `undefined`: a present
 * `undefined` element is indistinguishable from an absent one here.
 */
export function at<T>(array: readonly T[], index: number, what = "element"): T {
  const value = array.at(index);
  if (value === undefined) {
    throw new Error(
      `expected ${what} at index ${index}, but the array holds ${array.length}`,
    );
  }
  return value;
}
